import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, createIcon, Flex, Grid, Heading, LinkBox, LinkOverlay, List, ListItem, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { ArrowRightIcon, CheckedIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { getSchoolShortName } from '~/features/program/utils/get-school-data';

const SkillPictogramIcon = createIcon({
  displayName: 'SkillPictogramIcon',
  viewBox: '0 0 48 48',
  path: (
    <>
      <rect fill='#F6DFFF' height='48' rx='6' transform='matrix(1 0 0 -1 0 48)' width='48' />
      <path d='M31.5365 7.85547L30.0909 31.9389L11.4023 21.1491L31.5365 7.85547Z' fill='#931CF5' opacity='0.8' />
      <path d='M16.5586 40.9282L18.0042 16.8447L36.6927 27.6346L16.5586 40.9282Z' fill='#931CF5' opacity='0.5' />
    </>
  ),
});

export function SchoolsListSubsection() {
  const { t, i18n } = useTranslation();
  const schools = [
    {
      slug: 'artificial-intelligence',
      iconPath: '/images/school-icons/shields/artificial-intelligence.svg',
    },
    {
      slug: 'data-science',
      iconPath: '/images/school-icons/shields/data-science.svg',
    },
    {
      slug: 'business',
      iconPath: '/images/school-icons/shields/business.svg',
    },
    {
      slug: 'executive-leadership',
      iconPath: '/images/school-icons/shields/executive-leadership.svg',
    },
    {
      slug: 'programming',
      iconPath: '/images/school-icons/shields/programming.svg',
    },
    {
      slug: 'cloud-computing',
      iconPath: '/images/school-icons/shields/cloud-computing.svg',
    },
    {
      slug: 'autonomous-systems',
      iconPath: '/images/school-icons/shields/autonomous-systems.svg',
    },
    {
      slug: 'cybersecurity',
      iconPath: '/images/school-icons/shields/cybersecurity.svg',
    },
    {
      slug: 'product-management',
      iconPath: '/images/school-icons/shields/product-management.svg',
    },
    {
      slug: 'career-resources',
      iconPath: '/images/school-icons/shields/career-resources.svg',
    },
    {
      slug: 'animation-and-game-development',
      iconPath: '/images/school-icons/shields/animation-and-game-dev.svg',
    },
    {
      slug: 'devops',
      iconPath: '/images/school-icons/shields/devops.svg',
    },
  ];

  return (
    <Flex
      as='section'
      flexDir='column'
      gap='48px'
      borderTop={{
        md: '1px solid',
      }}
      borderTopColor={{
        md: 'grey.200',
      }}
      paddingTop={{
        md: '72px',
      }}
    >
      <Box textAlign='center'>
        <Heading
          as='p'
          color='grey.900'
          fontWeight='600'
          letterSpacing='-1px'
          lineHeight='1.1'
          mb={4}
          fontSize={{
            base: '1.75rem',
            md: '3.375rem',
          }}
        >
          {t('enterprise.overview.schools.title')}
        </Heading>
        <Text color='grey.500'>{t('enterprise.overview.schools.description')}</Text>
      </Box>
      <Grid as={List} gap='16px' gridTemplateColumns='repeat(auto-fill, minmax(min(100%, 340px), 1fr))'>
        {schools.map((school) => {
          const defaultName = getSchoolShortName(school.slug, t, 'en-US');
          const localizedName = getSchoolShortName(school.slug, t, i18n.language);
          return (
            <LinkBox
              key={school.slug}
              alignItems='center'
              as={ListItem}
              bgColor='white'
              borderRadius='4px'
              color='grey.500'
              display='flex'
              gap={3}
              padding='18px 26px 18px 20px'
              position='relative'
            >
              <Box flexShrink={0}>
                <Image alt='' height={20} src={school.iconPath} width={18} />
              </Box>
              <Text flex='1' fontWeight='600'>
                <LinkOverlay
                  href={`/school/${school.slug}`}
                  onClick={() =>
                    trackEvent({
                      name: 'Enterprise CTA Clicked',
                      type: 'click',
                      label: 'school_card_clicked',
                      value: defaultName,
                    })
                  }
                >
                  {localizedName}
                </LinkOverlay>
              </Text>
              <Box>
                <ArrowRightIcon color='grey.300' height='16px' width='16px' />
              </Box>
            </LinkBox>
          );
        })}
      </Grid>
      <Flex
        alignItems='center'
        borderTop='1px solid'
        borderTopColor='grey.200'
        gap={6}
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
        justifyContent={{
          base: 'center',
          md: 'space-between',
        }}
        paddingTop={{
          base: '32px',
          md: '40px',
        }}
      >
        <Flex alignItems='center' gap={6}>
          <SkillPictogramIcon
            height={{
              base: '40px',
              md: '48px',
            }}
            width={{
              base: '40px',
              md: '48px',
            }}
          />
          <Text
            fontSize={{
              base: '0.875rem',
              md: '1.125rem',
            }}
            lineHeight={{
              base: '1.4',
              md: '1.5',
            }}
          >
            {t('enterprise.overview.schools.descriptionText')}
          </Text>
        </Flex>
        <ButtonLink
          linkProps={{ href: '/get-started/enterprise' }}
          buttonProps={{
            size: 'left-icon',
            leftIcon: <CheckedIcon height='18px' width='18px' />,
            alignSelf: {
              md: 'normal',
            },
            onClick: () =>
              trackEvent({
                name: 'Enterprise CTA Clicked',
                type: 'click',
                label: 'unlock_team_potential_clicked',
              }),
          }}
        >
          {t('enterprise.overview.schools.unlockTeamPotential')}
        </ButtonLink>
      </Flex>
    </Flex>
  );
}
